import slugMod from "slug";

import { FunnelStep } from "./featureAPI";
import { UIFilter } from "./filter";
import { SatisfactionSpan } from "./schemas/satisfactionScore";
import { fromBase64, toBase64 } from "./utils/base64";

type Primitive =
  | number
  | number[]
  | string
  | string[]
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

export type UrlArgs = { id: string; name?: string };

export const serializeFilterParam = (value: UIFilter) => {
  return toBase64(value);
};

export const deserializeFilterParam = (value: string) => {
  return fromBase64<UIFilter | null>(value, null);
};

// Remove "undefined" string values from query params
export function cleanQueryParams(
  params?: string | Record<string, Primitive> | string[][] | URLSearchParams,
): URLSearchParams {
  if (
    !params ||
    typeof params === "string" ||
    params instanceof URLSearchParams ||
    Array.isArray(params)
  ) {
    return new URLSearchParams(params);
  }
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  }
  return searchParams;
}

export function slug({ id, name }: UrlArgs) {
  if (name) {
    return `${slugMod(name).substring(0, 15)}-${id}`;
  }
  return id;
}

export function LoginUrl(
  params?: string | Record<string, string> | string[][] | URLSearchParams,
) {
  const searchParams = cleanQueryParams(params);
  return `/login?${searchParams.toString()}`;
}

export function SignupUrl(
  params?: string | Record<string, string> | string[][] | URLSearchParams,
) {
  const searchParams = cleanQueryParams(params);
  return `/signup?${searchParams.toString()}`;
}

export function AppUrl(app: UrlArgs): string {
  return `/apps/${slug(app)}`;
}

export function EnvironmentUrl(environment: UrlArgs): string {
  return `/envs/${slug(environment)}`;
}

export function DebuggerUrl(
  environment: UrlArgs,
  params?: {
    feature?: string;
    company?: string;
    user?: string;
    eventTypes?: string[];
    search?: string;
  },
): string {
  const searchParams = cleanQueryParams(params);
  return (
    EnvironmentUrl(environment) + `/event-log/recent?${searchParams.toString()}`
  );
}

export function NewFeatureUrl(
  env: UrlArgs,
  params?: string | Record<string, string> | string[][] | URLSearchParams,
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/new?${searchParams.toString()}`;
}

export function FeatureUrl(
  env: UrlArgs,
  feature: UrlArgs,
  params?: { subsegment?: string | string[] },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/${slug(feature)}${
    params ? `?` + searchParams.toString() : ""
  }`;
}

export function FeatureAccessUrl(
  env: UrlArgs,
  feature: UrlArgs,
  params?: { subsegment?: string | string[] },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/${slug(
    feature,
  )}/access?${searchParams.toString()}`;
}

export function FeatureMonitorUrl(
  env: UrlArgs,
  feature: UrlArgs,
  params?: { period?: string },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/${slug(
    feature,
  )}/monitor?${searchParams.toString()}`;
}

export function FeatureSettingsUrl(
  env: UrlArgs,
  feature: UrlArgs,
  section: FeatureSettingsSections = "general",
  params?: { subsegment?: string | string[] },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/${slug(
    feature,
  )}/settings/${section}?${searchParams.toString()}`;
}

export function FeatureIntegrationsUrl(
  env: UrlArgs,
  feature: UrlArgs,
  params?: { subsegment?: string | string[] },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags/${slug(
    feature,
  )}/integrations?${searchParams.toString()}`;
}

export function FeaturesUrl(
  env: UrlArgs,
  params?: { subsegment?: string | string[] },
): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/flags?${searchParams.toString()}`;
}

export function FeatureViewUrl(env: UrlArgs, featureViewId: string): string {
  const searchParams = cleanQueryParams({ view: featureViewId });
  return `${EnvironmentUrl(env)}/flags?${searchParams.toString()}`;
}

export function CompanyUrl(env: UrlArgs, companyId: string): string {
  return `${EnvironmentUrl(env)}/companies/${encodeURIComponent(companyId)}`;
}

export function UserUrl(env: UrlArgs, userId: string): string {
  return `${EnvironmentUrl(env)}/users/${encodeURIComponent(userId)}`;
}

export function FeatureCompanyUrl(
  env: UrlArgs,
  companyId: string,
  params?: {
    search?: string;
  },
): string {
  const searchParams = cleanQueryParams(params);
  return `${CompanyUrl(env, companyId)}/flags?${searchParams.toString()}`;
}

// @deprecated use SegmentUrl instead
export function CompaniesUrl(
  env: UrlArgs,
  params?: { filter?: UIFilter; segmentId?: string; columns?: string[] },
): string {
  const searchParams = cleanQueryParams({
    filter: params?.filter ? serializeFilterParam(params?.filter) : undefined,
    segment: params?.segmentId,
    column: params?.columns,
  });
  return `${EnvironmentUrl(env)}/companies?${searchParams.toString()}`;
}

export function SegmentUrl(
  env: UrlArgs,
  segment: { id: string; name?: string | null },
  params?: {
    filter?: UIFilter;
    view?: "companies" | "users";
    column?: string[];
  },
): string {
  const searchParams = cleanQueryParams({
    filter: params?.filter ? serializeFilterParam(params.filter) : undefined,
    view: params?.view,
    column: params?.column,
  });
  return `${EnvironmentUrl(env)}/segments/${slug({ id: segment.id, name: segment.name ?? undefined })}${
    searchParams.toString() ? `?${searchParams.toString()}` : ""
  }`;
}

type FeedbackParams = {
  satisfaction?: SatisfactionSpan;
  feature?: string;
  company?: string;
  stars?: FunnelStep[];
  segment?: string;
};

export function FeedbackUrl(env: UrlArgs, params?: FeedbackParams): string {
  const searchParams = cleanQueryParams(params);
  return `${EnvironmentUrl(env)}/feedback?${searchParams.toString()}`;
}

export type GlobalSettingsSections =
  | "org-general"
  | "org-team"
  | "org-experiments"
  | "org-integrations"
  | "org-billing"
  | "org-archiving-flow"
  | "org-api-access"
  | "app-general"
  | "app-tracking"
  | "app-feature-views"
  | "app-segments"
  | "app-environments"
  | "app-stages"
  | "app-notifications"
  | "app-feedback"
  | "app-import"
  | "app-manage-data";

export type FeatureSettingsSections =
  | "general"
  | "usage"
  | "adoption"
  | "feedback"
  | "opt-in"
  | "slack"
  | "linear"
  | "archiving";

export function GlobalSettingsUrl(
  env: UrlArgs,
  section?: GlobalSettingsSections,
): string {
  return `${EnvironmentUrl(env)}/settings${section ? `/${section}` : ""}`;
}

export function GlobalSettingsStageUrl(env: UrlArgs, stageId: string): string {
  return `${GlobalSettingsUrl(env, "app-stages")}?stage=${stageId}`;
}

export function SalesEmail() {
  return "hello@reflag.com";
}

export function BillingPortalUrl(apiBaseUrl: string, orgId: string) {
  return `${apiBaseUrl}/orgs/${orgId}/billing-portal?returnUrl=${window.location.href}`;
}

export function AdminUrl(org?: UrlArgs) {
  if (org) {
    return `/admin/${slug(org)}`;
  }

  return `/admin`;
}

export function GenerateDemoAppUrl(org: UrlArgs): string {
  return `${OrgUrl(org)}/gen-demo-app`;
}

export function OrgUrl(org: UrlArgs): string {
  return `/orgs/${slug(org)}`;
}
